import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/use-auth';
import { XeroContext, type XeroContextValue } from '@/lib/xero-context';
import { NO_CONNECTION, type XeroConnection } from '@/lib/xero';

/*
 * Lives beside AuthProvider rather than in lib/, and exports only the
 * component: the hook stays in lib/xero-context.ts. That split is what keeps
 * react-refresh working — a module exporting both a component and a plain
 * function loses fast refresh for the whole file. Same arrangement as
 * AuthProvider and auth-context.
 */

/**
 * Reads the Xero connection once for the whole app.
 *
 * It is one fetch rather than one per screen because almost every detail page
 * can host a `<XeroPanel>`, and each of those needs the tenant's `shortCode`
 * to build an "Open in Xero" link. Fetching that per panel would put a request
 * behind every row of a list.
 *
 * The connection is read **once at mount and not polled**. It changes only
 * when someone connects or disconnects on `/integrations`, and that page calls
 * `reload()` itself — so polling would spend a request a minute to observe
 * something that changes twice a year.
 *
 * ⚠️ This provider is mounted app-wide, including on `/login`, where there is
 * no token — so the fetch must stay gated on `signedIn`, or `GET
 * /xero/connection` goes out with no `Authorization` header and 401s on every
 * visit to the sign-in screen.
 */
export function XeroProvider({ children }: { children: ReactNode }) {
  const [connection, setConnection] = useState<XeroConnection>(NO_CONNECTION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notInstalled, setNotInstalled] = useState(false);
  const [nonce, setNonce] = useState(0);
  const { user } = useAuth();
  const signedIn = user !== null;

  const reload = useCallback(() => {
    // setLoading belongs to the event that causes the refetch, not to the
    // effect: setting it inside the effect starts a second render every time
    // and trips react(set-state-in-effect).
    setLoading(true);
    setNonce((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!signedIn) return;

    const controller = new AbortController();
    const { signal } = controller;

    api
      .xeroConnection(signal)
      .then((connection) => {
        if (signal.aborted) return;
        setConnection(connection);
        setError(null);
        setNotInstalled(false);
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        /*
         * A 404 is not a failure here. It means the backend has not shipped
         * its `/xero` routes yet, which is the expected state until it does —
         * so it gets its own flag and a calm explanation rather than a red
         * banner on every page that mounts a panel.
         *
         * A 401 is left alone entirely: `api.ts` already attempts one refresh
         * and, failing that, ends the session. Reporting it here would put a
         * second, contradictory message on screen during sign-out.
         */
        if (caught instanceof ApiError && caught.status === 404) {
          setNotInstalled(true);
          setConnection(NO_CONNECTION);
          setError(null);
          return;
        }
        if (caught instanceof ApiError && caught.status === 401) return;
        setConnection(NO_CONNECTION);
        setError(caught instanceof Error ? caught.message : 'Could not read the Xero connection.');
      })
      .finally(() => {
        if (signal.aborted) return;
        setLoading(false);
      });

    return () => controller.abort();
  }, [nonce, signedIn]);

  /*
   * Signed out, the reading is DERIVED rather than stored: whatever the last
   * session read is not this session's answer, and the next person to sign in
   * on this machine must not see the previous tenant's name for a frame.
   *
   * Derived at render rather than reset in the effect, which would set state
   * synchronously inside it and start a cascading render — the thing
   * react(set-state-in-effect) exists to catch. `loading` is false because
   * "not asked for" is settled, not pending; a spinner would never resolve.
   */
  const value = useMemo<XeroContextValue>(
    () =>
      signedIn
        ? { connection, loading, error, notInstalled, reload }
        : { connection: NO_CONNECTION, loading: false, error: null, notInstalled: false, reload },
    [signedIn, connection, loading, error, notInstalled, reload],
  );

  return <XeroContext.Provider value={value}>{children}</XeroContext.Provider>;
}
